import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import { CheckCircle, Funnel } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { reservationLinks } from "@/components/lib/SidebarLinks";
import { Button } from "@/components/ui/button";
import { useSelector, useDispatch } from "react-redux";
import useAuth from "@/hooks/useAuth";
import StaffTableSkeleton from "@/components/skeleton/Staff-skeletion";
import { CardContent, Card } from "@/components/ui/card";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { format, parseISO, parse } from "date-fns";
import { fetchReservationById } from "@/redux/slice/reservation-slice/reservation-slice";
import { useParams } from "react-router-dom";
import {
  editReservation,
  acceptReservationThunk,
  rejectReservationThunk,
  clearReservationMessages
} from "@/redux/slice/reservation-slice/reservation-slice";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ReservationRejectDialog from "@/components/reservation/reservation-reject-modal";
import hasPermission from "@/hooks/hasPermission";

function ReservationRqDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [acceptDialog, setAcceptDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const { user } = useAuth();
  const { id } = useParams();
  const { singleReservation, singleLoading , success} = useSelector(
    (state) => state.reservation.reservation,
  );

  useEffect(() => {
    dispatch(fetchReservationById(id));
  }, [id]);

  const acceptHandler = async () => {
    dispatch(clearReservationMessages());
    const res = await dispatch(acceptReservationThunk(singleReservation?.id))
    if (acceptReservationThunk.fulfilled.match(res)) {
      toast.success("Reservation accepted successfully");
    } else if (acceptReservationThunk.rejected.match(res)) {
      toast.error(res.payload);
    }
  };

  if (singleLoading) return <StaffTableSkeleton links={reservationLinks} />;

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={reservationLinks} />}
      pageName={{ icon: true, title: "Reservation Info" }}
      paths={[
        { name: "Home", path: "/home" },
        {
          name: "Reservation Requests",
          path: "/reservation/reservation-request-list",
        },
        {
          name: "Reservation Details",
          path: "/reservation/details",
        },
      ]}
    >
      <div className="container flex flex-col mt-2">
        <div className="mt-5 flex flex-col space-y-2">
          <h3 className="font-semibold text-cwhite2">Customer Info</h3>
          <Card>
            <CardContent>
              <div className="mt-4 space-y-2">
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">
                    Customer Name
                  </span>
                  <span className="text-sm text-cwhite3">
                    : {singleReservation?.customer?.full_name || "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">
                    Phone Number
                  </span>
                  <span className="text-sm text-cwhite3">
                    : {singleReservation?.customer?.number || "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">Email</span>
                  <span className="text-sm text-cwhite3">
                    : {singleReservation?.customer?.email || "N/A"}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="mt-5 flex flex-col space-y-2">
          <h3 className="font-semibold text-cwhite2">Reservation Info</h3>
          <Card>
            <CardContent>
              <div className="mt-4 space-y-2">
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">
                    Reservation ID
                  </span>
                  <span className="text-sm text-cwhite3">
                    : {singleReservation?.id || "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">
                    Reservation Type
                  </span>
                  <span className="text-sm text-cwhite3">
                    : {singleReservation?.reservation_type == "phone" || singleReservation?.reservation_type == "phone_call" ? "Phone Call" : singleReservation?.reservation_type == "in_person" ? "In Person" : singleReservation?.reservation_type == "online" ? "Online" : singleReservation?.reservation_type == "other" ? "Other" : "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">
                    Reservation Date
                  </span>
                  <span className="text-sm text-cwhite3">
                    :{" "}
                    {singleReservation?.reservation_date
                      ? format(
                          parseISO(singleReservation?.reservation_date),
                          "yyyy-MM-dd",
                        )
                      : "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">Issue Date</span>
                  <span className="text-sm text-cwhite3">
                    {" "}
                    :{" "}
                    {singleReservation?.issue_date
                      ? format(
                          parseISO(singleReservation?.issue_date),
                          "yyyy-MM-dd",
                        )
                      : "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">Start Time</span>
                  <span className="text-sm text-cwhite3">
                    : {singleReservation?.start_date_time
                      ? format(
                          parseISO(singleReservation.start_date_time),
                          "yyyy-MM-dd hh:mm a",
                        )
                      : "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">End Time</span>
                  <span className="text-sm text-cwhite3">
                    : {singleReservation?.end_date_time
                      ? format(
                          parseISO(singleReservation.end_date_time),
                          "yyyy-MM-dd hh:mm a",
                        )
                      : "N/A"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">
                    Number Of Guest
                  </span>
                  <span className="text-sm text-cwhite3">
                    {" "}
                    : {singleReservation?.guest || "0"}
                  </span>
                </p>
                <p className="flex gap-2">
                  <span className="w-48 text-cwhite2 text-sm">
                    Advance Amount
                  </span>
                  <span className="text-sm text-cwhite3">
                    {" "}
                    : £{singleReservation?.advance || "0"}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex mt-5 items-center gap-5">
          {
            hasPermission("reservation.reservation_request.reservation_accept") && (
          
            <Button
              onClick={() => setAcceptDialog(true)}
              className=" text-cwhite bg-cgreen"
              disabled={singleReservation?.status === "confirmed"}
            >
              {singleReservation?.status === "confirmed" ? "Accepted" : "Accept"}
            </Button>
          )}

          {
            hasPermission("reservation.reservation_request.reservation_reject") && (
            <Button
              onClick={() => setRejectDialog(true)}
              className="bg-cred text-white"
            >
              Reject
            </Button>
          )}
        </div>
      </div>

      <DeleteConfirmDialog
        isLoading={false}
        isOpen={acceptDialog}
        onClose={() => setAcceptDialog(false)}
        onConfirm={() => {
          acceptHandler();
        }}
        yesBtnTxt="Yes"
        yesBtnClass="bg-cgreen hover:bg-cgreen/90"
        icon={<CheckCircle className="text-cgreen" />}
        description="Are you want to Accept this Reservation ?"
        title="Accept Reservation?"
      />
      <ReservationRejectDialog
        isOpen={rejectDialog}
        onClose={() => setRejectDialog(false)}
        reservation_id={singleReservation?.id}
      />
    </Layout2>
  );
}

export default ReservationRqDetails;
